import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import Slide from '@material-ui/core/Slide';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import Router from 'next/router';
import SearchIcon from '../../shared/icons/Search';
import AvatarIcon from '../../shared/icons/Avatar';
import CertificateIcon from '../../shared/icons/Certificate';
import Link, { External } from '../Link';
import NextLink from 'next/link';
import Menu from '../Menu';
import {
  LINKS,
  NAV_HEIGHT,
  LOGOS,
  CERTIFICATE_LINKS,
  HEADER_IMAGES,
  CONTEXT_LABELS,
} from './constants';
import { debounce } from 'lodash';

const getSubNavSection = (links, key) => {
  if (!key || !links) return null;
  return links.find(l => l.key === key).sections || [[], [], []];
};

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const getSubNavSectionColWidth = subNavSection => {
  if (subNavSection && subNavSection.length === 4) return 3;
  return 4;
};

const useStyles = makeStyles(theme => ({
  pushContent: {
    height: NAV_HEIGHT * 2,
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 100,
    // height: 200,
  },
  splitbg: {
    position: 'relative',
    height: props => props.height,
    '&:after, &:before': {
      content: '""',
      height: props => (props.height === 'auto' ? '100%' : props.height),
      width: '50%',
      position: 'absolute',
      top: 0,
      zIndex: -1,
    },
    '&:after': {
      right: 0,
      backgroundColor: props => props.rightColor,
    },
    '&:before': {
      left: 0,
      backgroundColor: props => props.leftColor,
    },
  },
  topBar: {
    height: NAV_HEIGHT,
  },
  wrap: {
    ...theme.mixins.maxWidth(),
    padding: theme.spacing(1),
    height: props => props.height,
    flexGrow: 1,
  },
  left: {
    backgroundColor: theme.palette.primary.main,
  },
  right: {
    backgroundColor: theme.palette.primary.light,
  },
  search: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.sketch.grey3,
    width: 240,
  },
  searchLabel: {
    color: theme.palette.sketch.grey3,
  },
  searchInputWrapper: {
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.sketch.grey3,
  },
  searchIcon: {
    color: theme.palette.sketch.grey3,
    padding: theme.spacing(0.5, 0, 0, 0),
  },
  searchInput: {
    padding: theme.spacing(0.5, 0, 0, 0),
    color: theme.palette.sketch.grey3,
    '&::placeholder': {
      color: theme.palette.sketch.grey3,
      opacity: 1,
    },
  },
  bottomBar: {
    height: NAV_HEIGHT,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
  },
  links: {
    display: 'flex',
    // justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
  },
  logoWrap: {
    cursor: 'pointer',
  },
  logo: {
    zIndex: 101,
    position: 'absolute',
    top: '50%',
    right: theme.spacing(1),
    transform: 'translate(0, -50%)',
    width: 80,
    height: 80,
    border: `10px solid ${theme.palette.common.white}`,
    borderRadius: '50%',
  },
  context: {
    position: 'absolute',
    right: theme.spacing(0),
    bottom: theme.spacing(-4),
    transform: 'translate(0, 100%)',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: theme.spacing(1),
    textTransform: 'capitalize',
  },
  photoEdge: {
    position: 'absolute',
    height: 32,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    backgroundSize: 'cover',
    backgroundPosition: 'bottom center',
  },
  btn: {
    height: '100%',
    color: theme.palette.common.white,
  },
  navToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
    cursor: 'default',
    padding: theme.spacing(0, 2),
    '&:hover, &.isActive': {
      backgroundColor: theme.palette.sketch.grey3,
    },
  },
  subNav: {
    zIndex: 100,
    padding: theme.spacing(1, 0),
    minHeight: 104,
    display: 'flex',
    alignItems: 'center',
  },
  subNavSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRight: `1px solid ${theme.palette.sketch.grey2}`,
    '&:last-child': {
      borderRight: 'none',
    },
  },
  empty: {
    borderRight: 'none',
  },
  sectionLink: {
    textAlign: 'center',
    cursor: 'default',
    margin: theme.spacing(0.5, 0),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

const TopBarButton = props => {
  const classes = useStyles(props);
  return (
    <Button
      className={classes.btn}
      variant="text"
      size="small"
      color="inherit"
      {...props}
    />
  );
};

const Toolbar = props => {
  const { leftColor, rightColor, className, ...restProps } = props;
  const classes = useStyles(props);
  return (
    <div className={cx(classes.splitbg, className)}>
      <div className={cx(classes.wrap)} {...restProps} />
    </div>
  );
};

Toolbar.defaultProps = {
  leftColor: '#fff',
  rightColor: '#fff',
  height: NAV_HEIGHT,
};

const SectionLink = props => {
  const { label, link, context, href, uuid, hideInNav, ...restProps } = props;
  if (hideInNav) return null;
  const classes = useStyles(props);
  if (!link && !href) {
    return (
      <Box py={0.5}>
        <Typography align="center" color="textSecondary" {...restProps}>
          {label}
        </Typography>
      </Box>
    );
  }
  if (href) {
    return (
      <External className={classes.sectionLink} href={href} {...restProps}>
        <Typography variant="body1">{label}</Typography>
      </External>
    );
  }

  const linkContext =
    process.env.NODE_ENV === 'production' ? '/' : `/enplus/${context}/`;

  return (
    <Link
      className={classes.sectionLink}
      href={uuid ? `${linkContext}[...pageId]` : `${linkContext}${link}`}
      as={`${linkContext}${link}`}
      {...restProps}
    >
      {label}
    </Link>
  );
};

SectionLink.defaultProps = {
  variant: 'body1',
  link: null,
};

const EntryLink = props => {
  const {
    link,
    currentSubNavSection,
    classes,
    linkContext,
    onCloseSubNav,
    onHoverNavToggle,
  } = props;
  if (!link.link) {
    return (
      <Typography
        variant="subtitle1"
        className={cx(classes.navToggle, {
          isActive: currentSubNavSection === link.key,
        })}
        onMouseEnter={e =>
          link.sections ? onHoverNavToggle(link.key) : onCloseSubNav()
        }
      >
        {link.label}
      </Typography>
    );
  }
  return (
    <Link
      variant="subtitle1"
      className={cx(classes.navToggle, {
        isActive: currentSubNavSection === link.key,
      })}
      onMouseEnter={e =>
        link.sections ? onHoverNavToggle(link.key) : onCloseSubNav()
      }
      href={link.link && `${linkContext}${link.link}`}
    >
      {link.label}
    </Link>
  );
};

const Navigation = props => {
  const { context, hideEdge } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentSubNavSection, setcurrentSubNavSection] = React.useState(false);
  const linkContext =
    process.env.NODE_ENV === 'production' ? '/' : `/enplus/${context}/`;

  const hasCertMenu = context === 'pellets';

  const handleMenuItemClick = item => {
    Router.push(`${linkContext}[...pageId]`, `${linkContext}${item.link}`);
  };

  const handleCertMenuButton = event => {
    if (hasCertMenu) {
      setAnchorEl(event.currentTarget);
    } else {
      handleMenuItemClick(CERTIFICATE_LINKS[context][0]);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  const closeSubNav = () => {
    setcurrentSubNavSection(false);
  };

  const debouncedCloseSubNav = React.useRef(debounce(closeSubNav, 800)).current;
  
  const handleHoverNavToggle = key => {
    debouncedCloseSubNav.cancel()
    setcurrentSubNavSection(key);
  };

  const handleSearch = e => {
    setSearchQuery(e.target.value);
  };

  const handleSubmit = e => {
    if (e.key === 'Enter') {
      Router.push({
        pathname: `${linkContext}unternehmen-finden`,
        query: {
          q: searchQuery,
        },
      });
    }
  };

  const theme = useTheme();
  const classes = useStyles(props);

  const links = LINKS[context];

  const subNavSection = getSubNavSection(links, currentSubNavSection);
  const subNavSectionColWidth = getSubNavSectionColWidth(subNavSection);

  return (
    <React.Fragment>
      <HideOnScroll>
        <header className={classes.header}>
          <Toolbar
            leftColor={theme.palette.primary.main}
            rightColor={theme.palette.primary.light}
            onMouseEnter={debouncedCloseSubNav}
          >
            <Grid container className={classes.topBar} spacing={2}>
              <Grid item xs={8} className={cx(classes.left)}>
                <FormControl
                  margin="dense"
                  variant="outlined"
                  className={classes.search}
                >
                  <Input
                    disableUnderline
                    className={classes.searchInputWrapper}
                    classes={{ input: classes.searchInput }}
                    id="standard-adornment-amount"
                    value={searchQuery}
                    placeholder="Zertifiziertes Unternehmen finden"
                    onChange={handleSearch}
                    onKeyDown={handleSubmit}
                    startAdornment={
                      <InputAdornment
                        position="start"
                        className={classes.searchIcon}
                      >
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    }
                  />
                </FormControl>

                <TopBarButton
                  startIcon={<CertificateIcon />}
                  aria-controls="certificate-menu"
                  aria-haspopup="true"
                  onClick={handleCertMenuButton}
                >
                  Zertifizieren lassen
                </TopBarButton>
                <NextLink href={`${linkContext}kontakt`} passHref>
                  <TopBarButton startIcon={<MailOutlineIcon />}>
                    Kontakt
                  </TopBarButton>
                </NextLink>
                <Menu
                  id="certificate-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  items={CERTIFICATE_LINKS[context]}
                  onItemClick={handleMenuItemClick}
                />
                <NextLink href={`${linkContext}login`} passHref>
                  <TopBarButton startIcon={<AvatarIcon />}>
                    Interner Bereich
                  </TopBarButton>
                </NextLink>
              </Grid>
              <Grid item xs={4} className={cx(classes.right)}>
                <TopBarButton
                  startIcon={<ShoppingCartOutlinedIcon />}
                  href="http://shop.depi.de/"
                >
                  DEPI-Shop
                </TopBarButton>
              </Grid>
            </Grid>
          </Toolbar>
          <Toolbar>
            <Grid container className={classes.bottomBar} spacing={2}>
              <Grid className={classes.links} item xs={12}>
                {links &&
                  links.map(l => (
                    <EntryLink
                      key={l.key}
                      link={l}
                      classes={classes}
                      currentSubNavSection={currentSubNavSection}
                      linkContext={linkContext}
                      onCloseSubNav={debouncedCloseSubNav}
                      onHoverNavToggle={handleHoverNavToggle}
                    />
                  ))}
              </Grid>
              <Link href={`${linkContext}`}>
                <div className={classes.logoWrap}>
                  <img src={LOGOS[context]} className={classes.logo} />
                </div>
              </Link>
            </Grid>
            <Typography
              component="div"
              variant="subtitle2"
              className={classes.context}
            >
              <b>{CONTEXT_LABELS[context]}</b>
            </Typography>
          </Toolbar>
          {!hideEdge && (
            <div
              className={classes.photoEdge}
              style={{
                backgroundImage:
                  context === 'holzbriketts'
                    ? 'none'
                    : `url(${context && HEADER_IMAGES(context)})`,
              }}
            />
          )}
          {subNavSection && (
            <Toolbar
              onMouseLeave={debouncedCloseSubNav}
              className={classes.subNav}
              height="auto"
              leftColor={theme.palette.sketch.grey3}
              rightColor={theme.palette.sketch.grey3}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid
                  item
                  xs={subNavSectionColWidth}
                  className={cx(classes.subNavSection, {
                    [classes.empty]: subNavSection?.[0]?.length === 0,
                  })}
                >
                  {subNavSection &&
                    subNavSection[0]?.map(l => (
                      <SectionLink key={l.link} context={context} {...l} />
                    ))}
                </Grid>
                <Grid
                  item
                  xs={subNavSectionColWidth}
                  className={cx(classes.subNavSection, {
                    [classes.empty]: subNavSection?.[2]?.length === 0,
                  })}
                >
                  {subNavSection &&
                    subNavSection[1]?.map(l => (
                      <SectionLink key={l.link} context={context} {...l} />
                    ))}
                </Grid>
                <Grid
                  item
                  xs={subNavSectionColWidth}
                  className={cx(classes.subNavSection, {
                    [classes.empty]: subNavSection?.[2]?.length === 0,
                  })}
                >
                  {subNavSection &&
                    subNavSection[2]?.map(l => (
                      <SectionLink key={l.link} context={context} {...l} />
                    ))}
                </Grid>
                {subNavSection[3] && (
                  <Grid
                    item
                    xs={subNavSectionColWidth}
                    className={cx(classes.subNavSection, {
                      [classes.empty]: subNavSection?.[3]?.length === 0,
                    })}
                  >
                    {subNavSection &&
                      subNavSection[3]?.map(l => (
                        <SectionLink key={l.link} context={context} {...l} />
                      ))}
                  </Grid>
                )}
              </Grid>
            </Toolbar>
          )}
        </header>
      </HideOnScroll>
      <div className={classes.pushContent} />
    </React.Fragment>
  );
};

Navigation.propTypes = {
  context: PropTypes.string,
  hideEdge: PropTypes.bool,
};

Navigation.defaultProps = {
  context: 'pellets',
  hideEdge: false,
};

export default Navigation;
